<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { useLoading } from "vue-loading-overlay";
import { errorNotify, successNotify } from "@/mixins";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";

const store = useStore();
const $loading = useLoading({});

const modelList = computed(() => store.getters['model/modelList']);
const cellSizeList = computed(() => store.getters['cell_size/cellSizeList']);
const models = computed(() => modelList.value?.results || []);

const sel = ref(null);
const active = computed(() => models.value.find((m) => m.code === sel.value) || null);

function loadModels() {
  const loader = $loading.show({});
  return store.dispatch('model/getModelList', '')
    .then(() => {
      if (!active.value) sel.value = models.value[0]?.code ?? null;
    })
    .finally(() => loader.hide());
}

onMounted(() => {
  loadModels();
  store.dispatch('cell_size/getCellSizeList', '');
});

// ---- rendering cells_map ({number: size}) as two racks ----
const cellNumbers = (m) =>
  Object.keys(m.cells_map || {}).map(Number).sort((a, b) => a - b);

// cell sizes are letters (S/M/L/XL from cell_size); span — grid row height,
// in slots: S = 1, M = 2, L = 4, XL = 6
const SIZE_SPAN = { S: 1, M: 2, L: 4, XL: 6 };
const spanOf = (size) => SIZE_SPAN[size] || (Number(size) > 0 ? Math.min(Number(size), 8) : 1);
const RACK_SLOTS = 16;

// a rack holds 16 slots — once the accumulated span sum reaches 16,
// a new rack starts (see the same algorithm in DevicesDetail.vue)
function racksOf(m) {
  const nums = cellNumbers(m);
  const result = [];
  let current = [];
  let slots = 0;
  nums.forEach((n) => {
    const span = spanOf(m.cells_map[n]);
    current.push({ n, size: m.cells_map[n], span });
    slots += span;
    if (slots >= RACK_SLOTS) {
      result.push(current);
      current = [];
      slots = 0;
    }
  });
  if (current.length) result.push(current);
  return result;
}

const totalCells = (m) => m.total_cells ?? cellNumbers(m).length;

// racks are grouped by 2 into a module (see .cmodule in device-models.scss —
// a module takes up 1/3 of the block width and doesn't stretch across the whole block)
function modulesOf(m) {
  const racks = racksOf(m);
  const modules = [];
  for (let i = 0; i < racks.length; i += 2) modules.push(racks.slice(i, i + 2));
  return modules;
}

// the width of the top mm ruler must match the actual width of the occupied
// modules (1/2/3 from .cmodule flex-basis), not the whole block.
// ruler-h is position:absolute inside .bp, so its "100%" is the full
// width of .bp (with padding), not the content width of .cmap, which is
// what the modules are actually computed from — compensate for .bp's padding (56+26=82px)
const BP_H_PADDING = 82;
const MODULE_GAP = 16;
function rulerWidthOf(m) {
  const n = Math.min(modulesOf(m).length, 3);
  if (n <= 0) return '0px';
  const pxSubtract = ((BP_H_PADDING + 2 * MODULE_GAP) * n) / 3 - (n - 1) * MODULE_GAP;
  return `calc(${n} / 3 * 100% - ${pxSubtract}px)`;
}

const SZ_LEGEND = [
  ['rgba(141,150,178,0.6)', 'S'],
  ['#7FA6FF', 'M'],
  ['#2E5BE6', 'L'],
  ['#0040DD', 'XL'],
];

const initials = (name) =>
  String(name || '').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// ---- editor modal ----
const SLOTS_PER_RACK = 16;
const modal = ref(null); // { mode: 'add'|'edit' }
const form = ref({});
const racks = ref([[], []]); // arrays of sizes
const selCell = ref(null);   // 'ri-ci'
const submitting = ref(false);

const sizeOptions = computed(() => {
  const sizes = (cellSizeList.value?.results || []).map((s) => String(s.size));
  const uniq = [...new Set(sizes)];
  return uniq.length ? uniq : ['S', 'M', 'L', 'XL'];
});

const rackSlots = (rack) => rack.reduce((s, x) => s + spanOf(x), 0);

const editorRacks = computed(() => {
  let i = 1;
  return racks.value.map((r) => r.map((size) => ({ n: i++, size, span: spanOf(size) })));
});

const selParts = computed(() => (selCell.value ? selCell.value.split('-').map(Number) : null));
const selectedCell = computed(() =>
  selParts.value ? editorRacks.value[selParts.value[0]]?.[selParts.value[1]] : null
);

function openModal(mode) {
  modal.value = { mode };
  selCell.value = null;
  if (mode === 'edit' && active.value) {
    const m = active.value;
    form.value = {
      code: m.code, name: m.name, height: m.height, width: m.width,
      weight: m.weight, description: m.description || '',
      main_cell_number: m.main_cell_number || 1,
    };
    racks.value = racksOf(m).map((rack) => rack.map((c) => c.size));
  } else {
    form.value = {
      code: '', name: '', height: 1900, width: 800, weight: 200, description: '',
      main_cell_number: 1,
    };
    racks.value = [['M', 'M'], ['M', 'M']];
  }
}

// cells are numbered positionally across both racks, so adding or removing one
// renumbers everything after it — keep main_cell_number pointing at the same
// physical cell instead of silently sliding onto its neighbour
const flatIndexOf = (ri, ci) => (ri === 0 ? ci : racks.value[0].length + ci);
const formCellCount = computed(() => racks.value[0].length + racks.value[1].length);

watch(formCellCount, (total) => {
  if (!total) return;
  if (form.value.main_cell_number > total) form.value.main_cell_number = total;
  if (!(form.value.main_cell_number >= 1)) form.value.main_cell_number = 1;
});

function setSize(size) {
  if (!selParts.value) return;
  const [ri, ci] = selParts.value;
  const others = rackSlots(racks.value[ri]) - spanOf(racks.value[ri][ci]);
  if (others + spanOf(size) > SLOTS_PER_RACK) return;
  racks.value[ri][ci] = size;
}

function addCell(ri) {
  if (rackSlots(racks.value[ri]) >= SLOTS_PER_RACK) return;
  const at = flatIndexOf(ri, racks.value[ri].length);
  racks.value[ri].push(sizeOptions.value[0]);
  if (form.value.main_cell_number - 1 >= at) form.value.main_cell_number += 1;
  selCell.value = `${ri}-${racks.value[ri].length - 1}`;
}

function delCell() {
  if (!selParts.value) return;
  const [ri, ci] = selParts.value;
  const at = flatIndexOf(ri, ci);
  racks.value[ri].splice(ci, 1);
  const mainIdx = form.value.main_cell_number - 1;
  if (mainIdx > at) form.value.main_cell_number -= 1;
  else if (mainIdx === at) form.value.main_cell_number = 1;
  selCell.value = null;
}

function setMain() {
  if (!selectedCell.value) return;
  form.value.main_cell_number = selectedCell.value.n;
}

watch(models, () => {
  if (!active.value && models.value.length) sel.value = models.value[0].code;
});

const formValid = computed(() =>
  String(form.value.code || '').trim() &&
  String(form.value.name || '').trim() &&
  formCellCount.value > 0 &&
  form.value.main_cell_number >= 1 &&
  form.value.main_cell_number <= formCellCount.value
);

function save() {
  if (!formValid.value || submitting.value) return;
  submitting.value = true;
  // cells_map format for the serializer: [{ number, size: { size } }]
  let n = 0;
  const cellsMap = racks.value.flatMap((rack) => rack.map((size) => ({ number: ++n, size: { size } })));
  const data = { ...form.value, main_cell_number: Number(form.value.main_cell_number), cells_map: cellsMap };
  const req = modal.value.mode === 'add'
    ? store.dispatch('model/createModel', data)
    : store.dispatch('model/updateModel', { id: form.value.code, data });
  req
    .then(() => {
      const err = modal.value.mode === 'add'
        ? store.getters['model/modelCreateError']
        : store.getters['model/modelUpdateError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify(modal.value.mode === 'add' ? 'Model added' : 'Model updated');
        sel.value = form.value.code;
        modal.value = null;
        loadModels();
      }
    })
    .finally(() => { submitting.value = false; });
}

// ---- delete ----
const del = ref(null);
function confirmDelete() {
  // The store action never rejects — it commits the API error instead, so
  // check the error getter. A model that can't be deleted answers 400 with
  // a display-ready `{"detail": "..."}`; show it as is.
  store.dispatch('model/deleteModel', del.value.code)
    .then(() => {
      const err = store.getters['model/modelDeleteError'];
      del.value = null;
      if (err) {
        errorNotify(err);
        return;
      }
      successNotify('Model deleted');
      sel.value = null;
      loadModels();
    });
}
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="openModal('add')">
      <span class="mdi mdi-plus"></span>
      Add Model
    </button>
  </Teleport>

  <div class="org-d3">
    <div class="orgwrap">
      <!-- master list -->
      <div class="orglist">
        <div class="orglist__head">
          <span class="mdi mdi-shape-outline" style="color: var(--lb-blue); font-size: 18px;"></span>
          <h2>Device Models</h2>
          <span class="count">{{ models.length }}</span>
        </div>
        <button
          v-for="m in models"
          :key="m.code"
          class="orgcard"
          :class="{ 'is-active': m.code === sel }"
          type="button"
          @click="sel = m.code">
          <div class="orgcard__logo">{{ initials(m.name) }}</div>
          <div class="orgcard__body">
            <div class="orgcard__name">{{ m.name }}</div>
            <div class="orgcard__meta">
              <span class="mdi mdi-barcode"></span>
              {{ m.code }}
            </div>
            <div class="orgcard__count">
              <span class="mdi mdi-view-grid-outline"></span>
              {{ totalCells(m) }} cells
            </div>
          </div>
        </button>
      </div>

      <!-- detail -->
      <div v-if="active" class="orgdetail">
        <div class="orgdetail__head">
          <div class="orgdetail__logo">{{ initials(active.name) }}</div>
          <div class="orgdetail__info">
            <h2>{{ active.name }}</h2>
            <div class="meta">
              <span><span class="mdi mdi-barcode"></span> {{ active.code }}</span>
              <span><span class="mdi mdi-view-grid-outline"></span> {{ totalCells(active) }} cells</span>
              <span><span class="mdi mdi-ruler-square-compass"></span> {{ active.width }}×{{ active.height }} mm</span>
            </div>
          </div>
          <button class="btn-ghost" type="button" @click="openModal('edit')">
            <span class="mdi mdi-pencil-outline"></span> Edit
          </button>
          <button class="iconbtn" title="Delete model" type="button" @click="del = active">
            <span class="mdi mdi-trash-can-outline"></span>
          </button>
        </div>

        <div class="dm-bpcard">
          <div class="dm-dbody">
            <div class="bp">
              <div class="ruler-h" :style="{ width: rulerWidthOf(active) }"><span>{{ active.width }} mm</span></div>
              <div class="ruler-v"><span>{{ active.height }} mm</span></div>
              <div class="cmap">
                <div v-for="(mod, mi) in modulesOf(active)" :key="mi" class="cmodule">
                  <div v-for="(rack, ri) in mod" :key="ri" class="crack">
                    <div
                      v-for="c in rack"
                      :key="c.n"
                      class="ccell"
                      :class="[`sz-${c.size}`, { main: c.n === active.main_cell_number }]"
                      :style="{ gridRow: `span ${c.span}` }">
                      <span class="cn">{{ c.n }}</span>
                      <span class="cs">{{ c.size }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="dm-info">
              <dl class="dm-dl">
                <dt>Code</dt><dd>{{ active.code }}</dd>
                <dt>Cells</dt><dd>{{ totalCells(active) }}</dd>
                <dt>Main cell</dt><dd>#{{ active.main_cell_number }}</dd>
                <dt>Height</dt><dd>{{ active.height }} mm</dd>
                <dt>Width</dt><dd>{{ active.width }} mm</dd>
                <dt>Weight</dt><dd>{{ active.weight }} kg</dd>
              </dl>
              <div v-if="active.description" class="dm-desc">{{ active.description }}</div>
              <div class="dm-szleg">
                <span v-for="[color, label] in SZ_LEGEND" :key="label" class="lg">
                  <span class="sw" :style="{ background: color }"></span>{{ label }}
                </span>
                <span class="lg"><span class="sw" style="background: #FFC400;"></span>Main cell</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="orgdetail">
        <div class="card empty-detail">
          <div>
            <span class="mdi mdi-shape-outline"></span>
            <p>No model selected</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- editor modal -->
  <Teleport to="body">
    <div v-if="modal" class="modal-overlay" @click.self="modal = null">
      <div class="modal modal--form modal--wide">
        <div class="modal__formhead">
          <h3 class="modal__title">{{ modal.mode === 'edit' ? 'Edit Device Model' : 'Add Device Model' }}</h3>
          <button class="modal__close" type="button" @click="modal = null">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
        <div class="modal__scroll">
          <div class="formgrid" style="padding: 0; margin-bottom: 22px;">
            <label class="field">
              <span class="field__label">Code <i>*</i></span>
              <input class="field__input" v-model="form.code" :disabled="modal.mode === 'edit'" placeholder="EL16R1">
            </label>
            <label class="field">
              <span class="field__label">Name <i>*</i></span>
              <input class="field__input" v-model="form.name" placeholder="Storage S24">
            </label>
            <label class="field">
              <span class="field__label">Height (mm)</span>
              <input class="field__input" type="number" v-model.number="form.height">
            </label>
            <label class="field">
              <span class="field__label">Width (mm)</span>
              <input class="field__input" type="number" v-model.number="form.width">
            </label>
            <label class="field">
              <span class="field__label">Weight (kg)</span>
              <input class="field__input" type="number" v-model.number="form.weight">
            </label>
            <label class="field">
              <span class="field__label">Main cell <i>*</i></span>
              <div class="field-select field-select--block">
                <select v-model.number="form.main_cell_number">
                  <option v-for="n in formCellCount" :key="n" :value="n">#{{ n }}</option>
                </select>
                <span class="mdi mdi-chevron-down caret"></span>
              </div>
            </label>
            <label class="field field--full">
              <span class="field__label">Description</span>
              <textarea class="field__input field__textarea" v-model="form.description"></textarea>
            </label>
          </div>

          <div style="font-size: 12.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--fg-2); margin: 4px 0 14px;">
            Cell map · {{ racks[0].length + racks[1].length }} cells
          </div>
          <div class="cmed">
            <div class="cmed__canvas">
              <div class="cmap">
                <div v-for="(rack, ri) in editorRacks" :key="ri" class="crack">
                  <div
                    v-for="(c, ci) in rack"
                    :key="c.n"
                    class="ccell"
                    :class="[`sz-${c.size}`, { sel: selCell === `${ri}-${ci}`, main: c.n === form.main_cell_number }]"
                    :style="{ gridRow: `span ${c.span}`, cursor: 'pointer' }"
                    @click="selCell = `${ri}-${ci}`">
                    <span class="cn">{{ c.n }}</span>
                    <span class="cs">{{ c.size }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="cmed__side">
              <div class="cmed__hint">
                Two racks · 16 slots each. Click a cell to select, then set its size. Add or remove cells per rack.
              </div>
              <div v-for="ri in [0, 1]" :key="ri" class="cmed__seg">
                <label>Rack {{ ri + 1 }} · {{ rackSlots(racks[ri]) }}/16 slots · {{ racks[ri].length }} cells</label>
                <button
                  class="btn-ghost"
                  style="height: 38px; justify-content: center;"
                  type="button"
                  :disabled="rackSlots(racks[ri]) >= SLOTS_PER_RACK"
                  @click="addCell(ri)">
                  <span class="mdi mdi-plus"></span> Add cell to rack {{ ri + 1 }}
                </button>
              </div>
              <div v-if="selectedCell" class="cmed__selinfo">
                <b>Cell #{{ selectedCell.n }} selected</b>
                <div class="cmed__seg" style="margin-top: 10px;">
                  <label>Size</label>
                  <div class="sizepick">
                    <button
                      v-for="s in sizeOptions"
                      :key="s"
                      :class="{ on: String(selectedCell.size) === s }"
                      type="button"
                      @click="setSize(s)">
                      {{ s }}
                    </button>
                  </div>
                </div>
                <div class="row">
                  <button
                    class="btn-ghost"
                    type="button"
                    :disabled="selectedCell.n === form.main_cell_number"
                    @click="setMain">
                    <span class="mdi" :class="selectedCell.n === form.main_cell_number ? 'mdi-star' : 'mdi-star-outline'"></span>
                    {{ selectedCell.n === form.main_cell_number ? 'Main cell' : 'Set as main' }}
                  </button>
                  <button class="btn-ghost" type="button" @click="delCell">
                    <span class="mdi mdi-trash-can-outline"></span> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal__actions modal__actions--right">
          <button class="btn-ghost" type="button" @click="modal = null">Cancel</button>
          <button class="btn-primary" type="button" :disabled="!formValid || submitting" @click="save">
            <span v-if="submitting" class="mdi mdi-loading mdi-spin"></span>
            {{ modal.mode === 'edit' ? 'Save changes' : 'Add model' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete model?"
    :message="del ? `“${del.name}” (${del.code}) will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
